import { formatAmount, getItemRarity } from "@loot-os/api-contract";
import { type ReactNode, useState } from "react";
import { Carousel } from "../../core/Carousel.js";
import { CAROUSEL_SLIDES } from "../../core/Carousel.styles.js";
import { Modal } from "../../core/Modal.js";
import { Amount } from "../../domain/Amount.js";
import { useCurrency } from "../../domain/currencyContext.js";
import { ItemArt } from "../../domain/ItemArt.js";
import { ItemCard } from "../../domain/ItemCard.js";
import { RARITY_FILL, rarityTier } from "../../domain/rarity.js";
import { cn } from "../../foundations/cn.js";
import type { RankedOpen } from "./content.js";

export interface PullRowProps {
  opens: readonly RankedOpen[];
  /**
   * `compact`: small cards in a strip, a glance. `cards`: each pull an ItemCard in its rarity, on a
   * carousel with Previous and Next beside `heading`.
   */
  look?: "compact" | "cards";
  /** Names the carousel (`cards`): the section's title. */
  label?: string;
  /** Drawn before the carousel's buttons (`cards`): the section's heading. */
  heading?: ReactNode;
  className?: string;
}

/** A pull's tier, by the platform's rarity of its value against what the open cost. */
function tierOf({ open }: RankedOpen) {
  return rarityTier(getItemRarity(open.item.value, open.costCents));
}

function TierLabel({ pull }: { pull: RankedOpen }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-body text-body-b3 text-text-secondary">
      <span
        aria-hidden
        className={cn("size-1.5 shrink-0 rounded-pill", RARITY_FILL[tierOf(pull)])}
      />
      {pull.tierLabel}
    </span>
  );
}

/**
 * The accessible name of a pull's card: the details label (which names the item), then what the
 * card shows in its order (WCAG 2.5.3), then the tier, which a pull card shows by its look alone.
 */
function cardLabel(pull: RankedOpen, value: string, look: "compact" | "cards") {
  return look === "cards"
    ? `${pull.detailsLabel} ${value} ${pull.tierLabel}`
    : `${pull.detailsLabel} ${pull.tierLabel} ${value}`;
}

/**
 * Pulls in a scrolling row, most valuable first: each a card that opens the item in a dialog. It
 * never links away; the details stay on the page. Renders nothing for `[]`.
 */
export function PullRow({ opens, look = "compact", label = "", heading, className }: PullRowProps) {
  const [shown, setShown] = useState<RankedOpen | null>(null);
  const currency = useCurrency();
  if (opens.length === 0) return null;
  const row =
    look === "cards" ? (
      <Carousel
        label={label}
        heading={heading}
        className={className}
        slideClassName={CAROUSEL_SLIDES.cards}
      >
        {opens.map((pull, i) => (
          <ItemCard
            variant="pull"
            key={pull.open.id}
            name={pull.open.item.name}
            image={pull.open.item.imageUrl}
            cents={pull.open.item.value}
            tier={tierOf(pull)}
            tierLabel={pull.tierLabel}
            priority={i < 6}
            seed={i}
            onSelect={() => setShown(pull)}
            label={cardLabel(pull, formatAmount(pull.open.item.value, currency), "cards")}
          />
        ))}
      </Carousel>
    ) : (
      <ul
        className={cn(
          "-mx-4 flex snap-x gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] md:mx-0 md:px-0",
          className,
        )}
      >
        {opens.map((pull) => (
          <li key={pull.open.id} className="w-18 shrink-0 snap-start md:w-22">
            <ItemCard
              variant="pull"
              size="compact"
              name={pull.open.item.name}
              image={pull.open.item.imageUrl}
              cents={pull.open.item.value}
              tier={tierOf(pull)}
              tierLabel={pull.tierLabel}
              onSelect={() => setShown(pull)}
              // The details label, then what the card shows, in its order: a voice user can say
              // the words they see (WCAG 2.5.3).
              label={cardLabel(pull, formatAmount(pull.open.item.value, currency), "compact")}
            />
          </li>
        ))}
      </ul>
    );
  return (
    <>
      {row}
      <Modal
        title={shown?.open.item.name ?? ""}
        open={shown !== null}
        onOpenChange={(open) => {
          if (!open) setShown(null);
        }}
        size="sm"
      >
        {shown && (
          <div className="flex flex-col items-center gap-4">
            <ItemArt src={shown.open.item.imageUrl} alt="" priority width={192} className="w-48" />
            <div className="flex w-full items-center justify-between gap-3">
              <TierLabel pull={shown} />
              <Amount cents={shown.open.item.value} size="lg" className="text-text-primary" />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
